/**
 * Maps slider travel to values.
 *
 * The native `<input type="range">` always runs on an integer *position* domain;
 * this converts that to and from the real value. Linear is the identity map.
 *
 * Log exists because money in this product spans four orders of magnitude. On a
 * linear ₹0–10 Cr slider, everything a salaried thirty-year-old might actually
 * hold sits inside the first 3% of travel — one pixel of thumb movement jumps
 * ₹30 lakh, and the control is useless on a phone. Log gives every decade the
 * same amount of travel.
 *
 * The `+1` offset is what lets a log scale start at zero: plain log(0) is
 * -Infinity, and a corpus of ₹0 is the single most common starting value for the
 * audience this is built for.
 */

export interface ScaleBound {
  min: number
  max: number
  scale: 'linear' | 'log'
}

/** Slider travel resolution. 1000 steps is finer than any phone can resolve by thumb. */
export const POSITION_STEPS = 1000

/** Value -> integer slider position. */
export function toPosition(value: number, bound: ScaleBound): number {
  const { min, max, scale } = bound
  if (max <= min) return 0

  const clamped = Math.min(max, Math.max(min, value))

  const fraction =
    scale === 'log'
      ? Math.log(clamped - min + 1) / Math.log(max - min + 1)
      : (clamped - min) / (max - min)

  return Math.round(fraction * POSITION_STEPS)
}

/** Integer slider position -> value. */
export function fromPosition(position: number, bound: ScaleBound): number {
  const { min, max, scale } = bound
  if (max <= min) return min

  const fraction = Math.min(1, Math.max(0, position / POSITION_STEPS))

  if (scale === 'linear') return min + fraction * (max - min)
  return min + Math.exp(fraction * Math.log(max - min + 1)) - 1
}

/**
 * Rounds a value to a sensible increment for its own magnitude.
 *
 * A log slider lands on values like ₹4,73,829, which is noise dressed as
 * precision — nobody's plan is accurate to the rupee. Snapping to roughly two
 * significant figures keeps the readout legible while staying fine enough that
 * dragging never appears to stick.
 */
export function snapMoney(value: number): number {
  if (value <= 0) return 0
  if (value < 1_000) return Math.round(value / 100) * 100

  const magnitude = Math.pow(10, Math.floor(Math.log10(value)) - 1)
  return Math.round(value / magnitude) * magnitude
}
